/**
 * GoogleReviewPrompt — TASK 11-2
 *
 * Modal / panel shown only from the hired (offer) outcome flow — never on its own.
 * "Maybe later" writes user_profiles.last_review_prompt_at = now() so the user
 * is not nudged again until 14 days have passed.
 *
 * Configure the Maps review deep link via VITE_GOOGLE_BUSINESS_REVIEW_URL
 * (Place ID / review URL from Google Business Profile).
 */
import { MapPin, X } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

import { supabase } from '../lib/supabase';

const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

/** Public review URL — set in env for production. */
export function getGoogleBusinessReviewUrl(): string {
  const u = import.meta.env.VITE_GOOGLE_BUSINESS_REVIEW_URL as string | undefined;
  return (u && u.trim()) ? u.trim() : 'https://www.google.com/maps/search/?api=1&query=GoGrabJob';
}

/**
 * Matches TASK 11-2: show when last_review_prompt_at IS NULL OR older than 14 days.
 */
export function isGoogleReviewDue(lastReviewPromptAt: string | null | undefined): boolean {
  if (lastReviewPromptAt == null || lastReviewPromptAt === '') return true;
  const t = new Date(lastReviewPromptAt).getTime();
  if (Number.isNaN(t)) return true;
  return Date.now() - t >= FOURTEEN_DAYS_MS;
}

/** Fetch eligibility for the signed-in user (no row ⇒ treat as due). */
export async function fetchShouldShowGoogleReviewPrompt(): Promise<boolean> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) return false;

  const { data } = await supabase
    .from('user_profiles')
    .select('last_review_prompt_at')
    .eq('id', session.user.id)
    .maybeSingle();

  if (!data) return true;
  return isGoogleReviewDue(data.last_review_prompt_at as string | null);
}

/**
 * TASK 11-2: "Maybe later" sets last_review_prompt_at = now().
 */
export async function snoozeGoogleReviewPrompt(): Promise<{ ok: boolean; error?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) return { ok: false, error: 'Not signed in' };

  const now = new Date().toISOString();
  const { error } = await supabase
    .from('user_profiles')
    .update({ last_review_prompt_at: now })
    .eq('id', session.user.id);

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export function openGoogleMapsReview(): void {
  const url = getGoogleBusinessReviewUrl();
  window.open(url, '_blank', 'noopener,noreferrer');
}

// ── Presentational panel (embedded inside ApplyConfirmationModal) ─────────

export interface GoogleReviewPromptPanelProps {
  onLeaveReview: () => void;
  onMaybeLater: () => void | Promise<void>;
  busy?: boolean;
}

export const GoogleReviewPromptPanel: React.FC<GoogleReviewPromptPanelProps> = ({
  onLeaveReview,
  onMaybeLater,
  busy = false,
}) => (
  <>
    <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b vscode-border">
      <div className="min-w-0">
        <h2 className="text-base font-semibold vscode-text-primary leading-snug flex items-center gap-2">
          <MapPin className="w-5 h-5 text-amber-500 shrink-0" aria-hidden />
          Enjoying GoGrabJob?
        </h2>
        <p className="text-xs vscode-text-secondary mt-0.5 leading-relaxed">
          If we helped you land this role, a quick Google review helps other job-seekers find us.
        </p>
      </div>
      <button
        type="button"
        onClick={() => void onMaybeLater()}
        aria-label="Close"
        disabled={busy}
        className="shrink-0 p-1.5 rounded-lg vscode-text-tertiary hover:bg-[var(--vscode-primary)]/8 transition-colors touch-manipulation disabled:opacity-40"
      >
        <X className="w-4 h-4" />
      </button>
    </div>

    <div className="px-5 py-5 flex flex-col gap-3">
      <button
        type="button"
        onClick={onLeaveReview}
        disabled={busy}
        className="w-full min-h-[48px] rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-40 touch-manipulation"
        style={{ background: 'var(--brand-purple, #6C47FF)' }}
      >
        Leave a Google review
      </button>
      <button
        type="button"
        onClick={() => void onMaybeLater()}
        disabled={busy}
        className="w-full min-h-[44px] rounded-xl text-sm vscode-text-secondary hover:bg-[var(--vscode-primary)]/5 transition-colors touch-manipulation disabled:opacity-40"
      >
        Maybe later
      </button>
    </div>
  </>
);

// ── Standalone modal (portal) — TASK 11-2 deliverable ─────────────────────

export interface GoogleReviewPromptProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Standalone floating modal. Prefer embedding {@link GoogleReviewPromptPanel}
 * inside the offer-outcome flow so testimonial + Google prompts stay sequenced.
 */
const GoogleReviewPrompt: React.FC<GoogleReviewPromptProps> = ({ isOpen, onClose }) => {
  const [busy, setBusy] = useState(false);

  const handleMaybeLater = useCallback(async () => {
    setBusy(true);
    try {
      await snoozeGoogleReviewPrompt();
    } finally {
      setBusy(false);
      onClose();
    }
  }, [onClose]);

  const handleLeave = useCallback(() => {
    openGoogleMapsReview();
    onClose();
  }, [onClose]);

  if (!isOpen || typeof document === 'undefined' || !document.body) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10020] flex items-center justify-center px-4 bg-gray-900/70 backdrop-blur-sm"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="google-review-title"
        className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border vscode-border bg-white dark:bg-gray-900 pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <span id="google-review-title" className="sr-only">
          Google review prompt
        </span>
        <GoogleReviewPromptPanel
          onLeaveReview={handleLeave}
          onMaybeLater={handleMaybeLater}
          busy={busy}
        />
      </div>
    </div>,
    document.body,
  );
};

export default GoogleReviewPrompt;
